<template>
  <div
    class="modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="new-q-title"
    @click.self="$emit('cancel')"
  >
    <div class="modal">
      <h2 id="new-q-title" class="modal-title">New question</h2>
      <p class="modal-subtitle">Pick the question type.</p>

      <div class="type-cards" role="radiogroup" aria-label="Question type">
        <button
          v-for="opt in options"
          :key="opt.value"
          type="button"
          role="radio"
          :aria-checked="selected === opt.value"
          :disabled="opt.disabled"
          :class="['type-card', {
            'is-active': selected === opt.value,
            'is-disabled': opt.disabled,
          }]"
          @click="opt.disabled ? null : (selected = opt.value)"
        >
          <div class="type-card-header">
            <span class="type-card-name">{{ opt.name }}</span>
            <span
              v-if="opt.disabled"
              class="coming-soon"
            >Coming soon</span>
          </div>
          <p class="type-card-desc">{{ opt.description }}</p>
        </button>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          @click="$emit('cancel')"
        >Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!selected"
          @click="onContinue"
        >Continue</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NewQuestionDialog',
  emits: ['cancel', 'continue'],
  data() {
    return {
      // Every editor here is wired up. Add `disabled: true` to any
      // type to take it off the menu without removing the entry.
      options: [
        {
          value: 'numeric_stepper',
          name: 'Numeric stepper',
          description: 'User picks an integer with +/− buttons. Example: portions per day.',
          disabled: false,
        },
        {
          value: 'single_choice',
          name: 'Single choice',
          description: 'User picks one option from a list. Example: how often do you eat fish?',
          disabled: false,
        },
        {
          value: 'matrix',
          name: 'Matrix',
          description: 'Multiple sub-items sharing the same scale. Example: rate each food group on a 1–5 scale.',
          disabled: false,
        },
        {
          value: 'short_text',
          name: 'Short text',
          description: 'User types a free-text answer. Example: postcode, nationality, "Other, please describe".',
          disabled: false,
        },
        {
          value: 'number',
          name: 'Number',
          description: 'User types an open number with a unit. Example: weight in kg, spend in CHF, cooking time. (Not the +/− counter — that\'s Numeric stepper.)',
          disabled: false,
        },
        {
          value: 'numeric_grid',
          name: 'Numeric grid',
          description: 'Several numbers on one card, one labelled field per row. Example: monthly household spend split across groceries, delivery, eating out and drinks; or height and weight together.',
          disabled: false,
        },
      ],
      // Pre-select the only enabled option so Continue is enabled on open.
      selected: 'numeric_stepper',
    };
  },
  methods: {
    onContinue() {
      if (!this.selected) return;
      this.$emit('continue', { question_type: this.selected });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-bg-muted: #f5f4ef;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
  font-family: var(--font-sans);
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px;
  color: var(--color-text-primary);
}

.modal-subtitle {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
}

.type-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.type-card {
  text-align: left;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.type-card:hover:not(.is-disabled) {
  border-color: var(--color-accent);
  background: var(--color-bg-soft);
}

.type-card.is-active {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.type-card.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
  background: var(--color-bg-soft);
}

.type-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.type-card-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text-primary);
}

.coming-soon {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  background: var(--color-bg-muted);
  padding: 2px 8px;
  border-radius: 4px;
}

.type-card-desc {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}
</style>
